// =============================================================================
// lib/storyViewed.ts — client-side "viewed stories" registry (P2 polish).
//
// The StoryViewer POSTs /stories/:id/view fire-and-forget, but Home's story ring
// is derived from the fetched `hasUnviewed` flag and nothing tells Home to
// refresh — so a group you just watched keeps its "unviewed" gradient ring until
// the next /stories fetch. This tiny store bridges that gap without a refetch (and
// without editing home.tsx): the viewer marks a group's userId here on close, and
// StoryRow reads/subscribes so its ring clears immediately.
//
// Follows the services/moderation.ts listener pattern (a Set + subscribe/emit).
// Purely in-memory + optimistic — the server view POST stays authoritative; this
// only reconciles the UI for the current session.
// =============================================================================

const _viewed = new Set<string>();
const _listeners = new Set<() => void>();

function emit() {
  for (const fn of _listeners) {
    try { fn(); } catch { /* a bad listener must not break the rest */ }
  }
}

/** Mark a story group (by userId) as fully viewed for this session. */
export function markGroupViewed(userId?: string | null): void {
  if (!userId || _viewed.has(userId)) return;
  _viewed.add(userId);
  emit();
}

/** Has this group been viewed in-session (so its ring should read as viewed)? */
export function isGroupViewed(userId?: string | null): boolean {
  return !!userId && _viewed.has(userId);
}

/** Subscribe to viewed-set changes; returns an unsubscribe fn. */
export function onStoryViewedChange(fn: () => void): () => void {
  _listeners.add(fn);
  return () => { _listeners.delete(fn); };
}
